import { decompressFrames, parseGIF } from 'gifuct-js';
import UPNG from 'upng-js';
import type { RgbaFrame } from './types';

export interface DecodedFrame extends RgbaFrame {
  index: number;
  delay: number;
}

export interface DecodedAnimation {
  width: number;
  height: number;
  frames: DecodedFrame[];
  format: 'GIF' | 'PNG' | 'APNG';
  truncated: boolean;
}

const MAX_FRAMES = 500;

function isPng(bytes: Uint8Array): boolean {
  return bytes.length >= 8 && bytes[0] === 0x89 && bytes[1] === 0x50 && bytes[2] === 0x4e && bytes[3] === 0x47;
}

function isGif(bytes: Uint8Array): boolean {
  return bytes.length >= 6 && String.fromCharCode(...bytes.subarray(0, 6)) === 'GIF89a' ||
    bytes.length >= 6 && String.fromCharCode(...bytes.subarray(0, 6)) === 'GIF87a';
}

function hasAcTl(bytes: Uint8Array): boolean {
  for (let i = 8; i + 8 <= bytes.length;) {
    const length = new DataView(bytes.buffer, bytes.byteOffset + i, 4).getUint32(0);
    const type = String.fromCharCode(...bytes.subarray(i + 4, i + 8));
    if (type === 'acTL') return true;
    i += 12 + length;
  }
  return false;
}

function copyFrame(data: ArrayBuffer, width: number, height: number, index: number, delay: number): DecodedFrame {
  return { width, height, data: new Uint8Array(data.slice(0)), index, delay: Math.max(0, delay) };
}

function decodePng(bytes: Uint8Array): DecodedAnimation {
  const input = bytes.buffer.slice(bytes.byteOffset, bytes.byteOffset + bytes.byteLength);
  const image = UPNG.decode(input);
  const rgba = UPNG.toRGBA8(image);
  const frames = rgba.slice(0, MAX_FRAMES).map((data, index) =>
    copyFrame(data, image.width, image.height, index, image.frames[index]?.delay ?? 0),
  );
  return { width: image.width, height: image.height, frames, format: hasAcTl(bytes) ? 'APNG' : 'PNG', truncated: rgba.length > MAX_FRAMES };
}

function blendPatch(canvas: Uint8Array, patch: Uint8ClampedArray, frameWidth: number, frameHeight: number, left: number, top: number, width: number): void {
  for (let y = 0; y < frameHeight; y++) {
    for (let x = 0; x < frameWidth; x++) {
      const source = (y * frameWidth + x) * 4;
      const target = ((top + y) * width + left + x) * 4;
      const alpha = patch[source + 3]! / 255;
      if (alpha === 1) {
        canvas.set(patch.subarray(source, source + 4), target);
      } else if (alpha > 0) {
        const inverse = 1 - alpha;
        canvas[target] = patch[source]! * alpha + canvas[target]! * inverse;
        canvas[target + 1] = patch[source + 1]! * alpha + canvas[target + 1]! * inverse;
        canvas[target + 2] = patch[source + 2]! * alpha + canvas[target + 2]! * inverse;
        canvas[target + 3] = 255 * (alpha + canvas[target + 3]! / 255 * inverse);
      }
    }
  }
}

function clearPatch(canvas: Uint8Array, frameWidth: number, frameHeight: number, left: number, top: number, width: number): void {
  for (let y = 0; y < frameHeight; y++) canvas.fill(0, ((top + y) * width + left) * 4, ((top + y) * width + left + frameWidth) * 4);
}

function decodeGif(bytes: Uint8Array): DecodedAnimation {
  const parsed = parseGIF(bytes.buffer.slice(bytes.byteOffset, bytes.byteOffset + bytes.byteLength));
  const sourceFrames = decompressFrames(parsed, true);
  const canvas = new Uint8Array(parsed.lsd.width * parsed.lsd.height * 4);
  const frames: DecodedFrame[] = [];
  for (const [index, source] of sourceFrames.entries()) {
    if (index >= MAX_FRAMES) break;
    const previous = source.disposalType === 3 ? canvas.slice() : null;
    blendPatch(canvas, source.patch, source.dims.width, source.dims.height, source.dims.left, source.dims.top, parsed.lsd.width);
    frames.push({ width: parsed.lsd.width, height: parsed.lsd.height, data: canvas.slice(), index, delay: Math.max(0, source.delay) });
    if (source.disposalType === 2) clearPatch(canvas, source.dims.width, source.dims.height, source.dims.left, source.dims.top, parsed.lsd.width);
    else if (source.disposalType === 3 && previous) canvas.set(previous);
  }
  return { width: parsed.lsd.width, height: parsed.lsd.height, frames, format: 'GIF', truncated: sourceFrames.length > MAX_FRAMES };
}

export async function decodeAnimation(file: Blob): Promise<DecodedAnimation> {
  const bytes = new Uint8Array(await file.arrayBuffer());
  if (isGif(bytes)) return decodeGif(bytes);
  if (isPng(bytes)) return decodePng(bytes);
  throw new Error('仅支持 GIF、PNG 或 APNG 文件');
}

export { MAX_FRAMES };
